/* eslint-disable camelcase */

import React, { useContext, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  HvGlobalActions,
  HvGrid,
  HvTypography,
  HvButton,
  HvTooltip,
} from "@hitachivantara/uikit-react-core";

import {
  useHvTable,
  useHvPagination,
  useHvSortBy,
  HvTableColumnConfig,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";
import {
  Play,
  PlayVideoDetails,
  CurrentStep,
  Level3Bad,
} from "@hitachivantara/uikit-react-icons";

import { IncidentContext } from "lib/context/IncidentContext";
import SimpleTable, {
  actionsColumn,
  DEFAULT_PAGE,
  numberColumn,
  textColumn,
} from "components/common/SimpleTable";
import { Camera } from "lib/api";
import { numberFallback, stringFallback } from "lib/utils/fallbacks";
import { OverflowTooltip } from "components/common";
import useStyles from "./styles";

const DEFAULT_PAGE_SIZE = 5;

const DEFAULT_SORT = [
  {
    id: "distance_from_incident_location",
    desc: false,
  },
];

const cameraStatusColors = {
  GatewayOffline: "#aa00ff",
  Deleted: "#ffc602",
  Online: "#27ae60",
  Offline: "#c0392b",
};

const getCameraStatus = (camera: Camera) => {
  const { attributes } = camera;

  if (
    !!attributes.is_gateway_active &&
    !(attributes.is_gateway_active as { value: boolean }).value
  ) {
    return "GatewayOffline";
  }

  if (
    !!attributes.is_detached &&
    (attributes.is_detached as { value: boolean }).value
  ) {
    return "Deleted";
  }

  if (attributes.active) {
    const { value } = attributes.active as { value: boolean };
    return value ? "Online" : "Offline";
  }

  return "Unknown";
};
const getCameraIsRecording = (camera: Camera) => {
  const { attributes } = camera;
  const can_playback = attributes.can_playback
    ? (attributes.can_playback as { value: boolean }).value
    : undefined;
  // if can_playback is set use that value otherwise use recording_enabled value if set otherwise false
  return can_playback !== undefined
    ? can_playback
    : attributes.recording_enabled
    ? (attributes.recording_enabled as { value: boolean }).value
    : false;
};

const isRTSPCamera = (camera: Camera) => {
  const { attributes } = camera;

  if (
    !!attributes.type &&
    ((attributes.type as { value: string }).value === "ip" ||
      (attributes.type as { value: string }).value === "onvif")
  ) {
    return true;
  }

  return false;
};

const IncidentEvents: React.FC = () => {
  const { t } = useTranslation("map");
  const classes = useStyles();
  const history = useHistory();
  const {
    incident: {
      inc_nearest_cameras = [],
      create_timestamp = "",
      inc_name = "",
    } = {},
  } = useContext(IncidentContext);
  const columns = useMemo(
    (): HvTableColumnConfig<Camera>[] => [
      textColumn({
        Header: t("sections.cameras.columns.name") as string,
        accessor: "name",
        style: { width: "50%" },
        sortType: (rowA, rowB) => {
          const a = rowA.original.name.toLowerCase();

          const b = rowB.original.name.toLowerCase();

          if (a > b) return 1;

          if (b > a) return -1;

          return 0;
        },
      }),
      numberColumn({
        Header: t("sections.cameras.columns.distance") as string,
        accessor: "distance_from_incident_location",
        style: { width: "25%" },
        Cell: ({ value }: HvCellProps<Camera, number>) =>
          numberFallback(Number(value.toFixed(1))),
      }),
      {
        Header: t("sections.cameras.columns.status") as string,
        id: "status",
        disableSortBy: true,
        style: { width: "25%" },
        Cell: ({ row }: HvCellProps<Camera>) => {
          const Wrapper: React.FC = ({ children }) => (
            <div className={classes.status}>{children}</div>
          );

          const Status: React.FC<{ color: string; label: string }> = ({
            color,
            label,
          }) => (
            <Wrapper>
              <CurrentStep
                width={16}
                height={16}
                className={classes.statusIcon}
                color={[color]}
              />
              <OverflowTooltip data={stringFallback(label)} />
            </Wrapper>
          );

          const cameraStatus = getCameraStatus(row.original);

          if (cameraStatus !== "Unknown") {
            return (
              <Status
                color={cameraStatusColors[cameraStatus]}
                label={t(`sections.cameras.columns.status${cameraStatus}`)}
              />
            );
          }

          return (
            <Wrapper>
              <Level3Bad
                width={16}
                height={16}
                className={classes.statusIcon}
              />
              <OverflowTooltip
                data={stringFallback(
                  t(`sections.cameras.columns.statusUnknown`)
                )}
              />
            </Wrapper>
          );
        },
      },
      actionsColumn({
        id: "livePlay",
        style: { width: 64, maxWidth: 64 },
        disableSortBy: true,
        Cell: ({ row }: HvCellProps<Camera>) => {
          const cameraStatus = getCameraStatus(row.original);
          const cameraIsRecording = getCameraIsRecording(row.original);

          const disablePlayback =
            (cameraStatus !== "Online" && cameraStatus !== "Unknown") ||
            isRTSPCamera(row.original) ||
            !cameraIsRecording;
          return (
            <div style={{ display: "flex" }}>
              <HvTooltip
                title={
                  <HvTypography>
                    {t("sections.cameras.columns.live")}
                  </HvTypography>
                }
              >
                <div>
                  <HvButton
                    aria-label={t("sections.cameras.columns.live")}
                    icon
                    disabled={
                      cameraStatus !== "Online" && cameraStatus !== "Unknown"
                    }
                    onClick={() =>
                      history.push({
                        pathname: `/camera/${row.id}`,
                        search: "?pageToRender=Live",
                      })
                    }
                  >
                    <Play />
                  </HvButton>
                </div>
              </HvTooltip>
              <HvTooltip
                title={
                  <HvTypography>
                    {t("sections.cameras.columns.playback")}
                  </HvTypography>
                }
              >
                <div>
                  <HvButton
                    aria-label={t("sections.cameras.columns.playback")}
                    icon
                    disabled={disablePlayback}
                    onClick={() =>
                      history.push({
                        pathname: `/camera/${row.id}`,
                        search: `?pageToRender=Archive&name=${inc_name}&timestamp=${create_timestamp}`,
                      })
                    }
                  >
                    <PlayVideoDetails />
                  </HvButton>
                </div>
              </HvTooltip>
            </div>
          );
        },
      }),
    ],
    [classes.status, classes.statusIcon, create_timestamp, history, inc_name, t]
  );

  const initialState = useMemo(
    () => ({
      pageIndex: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: DEFAULT_SORT,
    }),
    []
  );

  const instance = useHvTable<Camera>(
    {
      columns,
      data: inc_nearest_cameras,
      getRowId: useCallback((v: Camera): string => `${v.id}`, []),
      pageCount: Math.ceil(
        (inc_nearest_cameras.length ?? 0) / (initialState?.pageSize ?? 1)
      ),
      initialState,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination
  );

  return (
    <HvGrid container>
      <HvGrid item xs={12}>
        <HvGlobalActions
          title={t("sections.cameras.title")}
          variant="section"
        />
        <HvTypography variant="normalText">
          {t("sections.cameras.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid item xs={12}>
        <SimpleTable
          instance={instance}
          totalCount={inc_nearest_cameras.length}
          disableSelection
          defaultPageSize={DEFAULT_PAGE_SIZE}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentEvents;
